import React, { useEffect, useRef, useState } from "react";
import AppLayout from "../components/AppLayout";
import TalkingHead from "../components/TalkingHead";
import VoiceBar from "../components/VoiceBar";
import { useVoice } from "../context/VoiceContext";
import { api } from "../lib/api";
import { Send, Plus, Trash2, Volume2 } from "lucide-react";
import { toast } from "sonner";
import MicButton from "../components/MicButton";

const AVATAR = "https://customer-assets.emergentagent.com/job_ai-agent-builder-135/artifacts/s8r4h7c5_%CE%A3%CF%84%CE%B9%CE%B3%CE%BC%CE%B9%CF%8C%CF%84%CF%85%CF%80%CE%BF%20%CE%BF%CE%B8%CF%8C%CE%BD%CE%B7%CF%82%202026-04-20%20214621.png";

export default function Chat() {
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const { speak, autoSpeak, muted } = useVoice() || {};

  const loadSessions = async () => {
    try {
      const r = await api.get("/api/chat/sessions");
      const sessionsList = Array.isArray(r.data) ? r.data : (r.data?.sessions || []);
      setSessions(sessionsList);
      if (sessionsList.length && !activeId) selectSession(sessionsList[0].session_id);
    } catch (err) {
      console.error("Failed to load sessions:", err);
      setSessions([]); // Safety: never leave undefined
    }
  };

  const selectSession = async (id) => {
    setActiveId(id);
    const r = await api.get(`/api/chat/sessions/${id}/messages`);
    setMessages(r.data);
  };

  const newSession = async () => {
    const r = await api.post("/api/chat/sessions");
    setSessions((s) => [r.data, ...s]);
    setActiveId(r.data.id);
    setMessages([]);
  };

  const deleteSession = async (id) => {
    await api.delete(`/api/chat/sessions/${id}`);
    setSessions((s) => s.filter((x) => x.id !== id));
    if (activeId === id) { setActiveId(null); setMessages([]); }
  };

  const send = async () => {
    if (!input.trim() || busy) return;
    const text = input;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { id: "tmp", role: "user", content: text, session_id: activeId }]);
    try {
      const r = await api.post("/api/chat/send", { session_id: activeId, content: text, mode: "chat" });
      setActiveId(r.data.session_id);
      const fresh = await api.get(`/api/chat/sessions/${r.data.session_id}/messages`);
      setMessages(fresh.data);
      loadSessions();
      if (autoSpeak && !muted && r.data.assistant_message?.content) {
        speak(r.data.assistant_message.content);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something broke");
    } finally { setBusy(false); }
  };

  useEffect(() => { loadSessions(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  // Pickup pre-filled prompt from CRM follow-up widget (Dashboard)
  useEffect(() => {
    try {
      const pre = localStorage.getItem("gio_chat_prefill");
      if (pre) { setInput(pre); localStorage.removeItem("gio_chat_prefill"); }
    } catch (_) {}
  }, []);

  return (
    <AppLayout title="Chat" overline="010 · CONVERSATION · EN / ΕΛΛ">
      <div className="mb-6 flex items-center justify-between gap-4 flex-wrap fade-up">
        <div className="flex items-center gap-4">
          <TalkingHead size={80} />
          <div>
            <div className="overline text-[color:var(--gio-primary)]">GIOAGENT · LIVE</div>
            <div className="font-heading text-2xl text-white">Ready, κούκλε.</div>
          </div>
        </div>
        <VoiceBar />
      </div>
      <div className="grid lg:grid-cols-[260px_1fr] gap-6 min-h-[60vh]" data-testid="chat-root">
        {/* Sessions */}
        <aside className="border border-zinc-900 bg-zinc-950 flex flex-col">
          <button onClick={newSession} data-testid="new-session-button"
            className="flex items-center gap-2 p-4 text-[color:var(--gio-primary)] font-mono-ui text-[11px] tracking-[0.2em] uppercase border-b border-zinc-900 hover:bg-zinc-900/40 transition">
            <Plus size={14} /> New session
          </button>
          <div className="flex-1 overflow-y-auto">
            {(sessions || []).map((s) => (
              <div key={s.session_id} data-testid={`session-${s.session_id}`}
                className={`group flex items-center justify-between gap-2 px-4 py-3 text-sm cursor-pointer border-b border-zinc-900/60 ${activeId === s.session_id ? "bg-zinc-900/40 text-white" : "text-zinc-500 hover:text-white"}`}
                onClick={() => selectSession(s.session_id)}>
                <div className="truncate">{s.last_message || "New session"}</div>
                <button onClick={(e) => { e.stopPropagation(); deleteSession(s.session_id); }} data-testid={`delete-session-${s.session_id}`}
                  className="text-zinc-700 hover:text-[color:var(--gio-primary)] opacity-0 group-hover:opacity-100 transition">
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
            {(sessions || []).length === 0 && <div className="p-4 overline">No sessions yet</div>}
          </div>
        </aside>

        {/* Main chat */}
        <section className="border border-zinc-900 bg-black flex flex-col">
          <div className="flex-1 overflow-y-auto p-6 space-y-6" data-testid="chat-messages">
            {messages.length === 0 && (
              <div className="h-full flex flex-col items-center justify-center text-center py-16">
                <img src={AVATAR} alt="" className="w-20 h-20 object-cover border border-zinc-900 mb-6 opacity-80" />
                <div className="overline mb-3">GIOAGENT · READY</div>
                <div className="font-heading text-3xl text-white mb-2">Τι φτιάχνουμε σήμερα;</div>
                <div className="text-zinc-500 text-sm max-w-sm">Ask for hooks, rewrite a bio, plan a content week, draft sexting replies — in EN or EL.</div>
              </div>
            )}
            {messages.map((m, idx) => (
              <div key={m.id || idx} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.role}-${idx}`}>
                <div className={`max-w-[80%] p-4 text-sm leading-relaxed whitespace-pre-wrap group relative ${
                  m.role === "user"
                    ? "bg-zinc-900 border border-zinc-800 text-white rounded-tr-none"
                    : "bg-transparent border border-[color:var(--gio-primary)] text-[color:var(--gio-primary)] rounded-tl-none shadow-[0_0_15px_rgba(255,51,0,0.15)]"
                }`}>
                  {m.content}
                  {m.role === "assistant" && (
                    <button onClick={() => speak && speak(m.content)} data-testid={`msg-speak-${idx}`}
                      className="absolute -top-2 -right-2 p-1 bg-black border border-[color:var(--gio-primary)] opacity-0 group-hover:opacity-100 transition text-[color:var(--gio-primary)]">
                      <Volume2 size={12} />
                    </button>
                  )}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start" data-testid="chat-typing">
                <div className="border border-[color:var(--gio-primary)] text-[color:var(--gio-primary)] p-4 text-xs font-mono-ui tracking-widest">GIOAGENT IS TYPING...</div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          <div className="border-t border-zinc-900 p-4 flex gap-3 items-end">
            <MicButton onTranscript={(t) => setInput((prev) => (prev ? prev + " " : "") + t)} testid="chat-mic" />
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Μίλα μου... / Talk to me... (🎙 hold Mic για φωνή)"
              data-testid="chat-input"
              rows={2}
              className="flex-1 bg-zinc-950 border border-zinc-900 text-white p-3 text-sm resize-none focus:outline-none focus:border-[color:var(--gio-primary)] transition"
            />
            <button onClick={send} disabled={busy || !input.trim()} data-testid="chat-send-button"
              className="btn-primary flex items-center gap-2 disabled:opacity-40">
              <Send size={16} /> Send
            </button>
          </div>
        </section>
      </div>
    </AppLayout>
  );
}
